import { useCallback, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export interface ConfirmRequest {
  title: string;
  body?: string;
  /** Label for the affirmative button. Say what will happen, not "OK". */
  confirmLabel?: string;
  cancelLabel?: string;
  /** Red confirm button, for anything that destroys content. */
  destructive?: boolean;
}

/**
 * Promise-based confirmation.
 *
 * `window.confirm` was doing this job for deletes: it blocks the whole renderer, cannot
 * be styled, and on a phone it renders as a browser chrome sheet that looks nothing like
 * the app. This is shadcn's AlertDialog (Radix), so it traps focus, closes on Escape, and
 * defaults focus to Cancel — which matters when the alternative is deleting a lesson.
 *
 * Usage:
 *   const { confirm, dialog } = useConfirm();
 *   if (await confirm({ title: 'Delete this?', destructive: true })) …
 *   return <>{…}{dialog}</>;
 */
export function useConfirm() {
  const [request, setRequest] = useState<ConfirmRequest | null>(null);
  // The resolver of the promise handed back to the caller; called with the answer.
  const [resolver, setResolver] = useState<((ok: boolean) => void) | null>(null);

  const confirm = useCallback((req: ConfirmRequest) => {
    setRequest(req);
    return new Promise<boolean>((resolve) => setResolver(() => resolve));
  }, []);

  const settle = useCallback(
    (ok: boolean) => {
      resolver?.(ok);
      setResolver(null);
      setRequest(null);
    },
    [resolver],
  );

  const dialog = (
    <ConfirmDialog
      request={request}
      onConfirm={() => settle(true)}
      onCancel={() => settle(false)}
    />
  );

  return { confirm, dialog };
}

function ConfirmDialog({
  request,
  onConfirm,
  onCancel,
}: {
  request: ConfirmRequest | null;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <AlertDialog
      open={!!request}
      // Dismissing by Escape or overlay must resolve the promise too, or the caller
      // waits forever and the UI looks frozen.
      onOpenChange={(open) => !open && onCancel()}
    >
      <AlertDialogContent style={{ maxWidth: 'min(460px, calc(100% - 2rem))' }}>
        <AlertDialogHeader>
          <AlertDialogTitle style={{ fontFamily: 'var(--font-display)', color: 'var(--text-strong)' }}>
            {request?.title}
          </AlertDialogTitle>
          {request?.body && (
            <AlertDialogDescription style={{ color: 'var(--text-muted)' }}>
              {request.body}
            </AlertDialogDescription>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel asChild>
            <button type="button" className="vu-btn vu-btn-ghost" onClick={onCancel}>
              {request?.cancelLabel ?? 'Cancel'}
            </button>
          </AlertDialogCancel>
          <AlertDialogAction asChild>
            <button
              type="button"
              className="vu-btn"
              data-destructive={request?.destructive ? 'true' : undefined}
              onClick={onConfirm}
            >
              {request?.confirmLabel ?? 'Confirm'}
            </button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
